import { Box, InputAdornment, Stack, TextField, ThemeProvider, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { dayjs, extract, isNotNullOrUndefined, withDefault } from '@weft/utils';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { muiDefaultTheme } from '../../../../assets/themes/default-theme';
import { useFormErrorNotificationContext } from '../../../../contexts/form-error-notification-context/form-error-notification-context';
import { useCustomersOverviews } from '../../../../hooks/requests/use-customers-overviews/use-customers-overviews';
import { PlanSettingsSelect, PlanSettingsSelectMode } from '../../../plans/CreatePlanPage/create-plan-page-components/PlanSettingsSelect';
import { TrialDurationAutoComplete } from '../../../plans/CreatePlanPage/create-plan-page-components/TrialDurationAutoComplete';
import type { InvoiceFormInput } from './CreateInvoiceForm/create-invoice-form-types/invoice-form-input';

export const CreateInvoiceSettings: React.FC = () => {
  const { control, register, watch, setValue } = useFormContext<InvoiceFormInput>();
  const { registerErrorField } = useFormErrorNotificationContext();
  const { data: customersOverviewsResponse } = useCustomersOverviews();
  const customers = extract(withDefault(customersOverviewsResponse?.customers, []), ({ id, name }) => ({ id, name }));

  const customerIdRegister = register('settings.customerId');
  const startPeriodRegister = register('settings.startPeriod');
  const paymentDueDaysRegister = register('settings.paymentDueDays');
  const trialPeriodRegister = register('settings.trialPeriod');
  const purchaseOrderRegister = register('settings.purchaseOrder');

  registerErrorField(customerIdRegister.name);

  const customerName = watch(customerIdRegister.name);
  const paymentTerms = watch(paymentDueDaysRegister.name);

  const handleCustomerIdChange = (newCustomerId: string) => {
    setValue(customerIdRegister.name, newCustomerId);
  };

  const handlePaymentDueDaysChange = (newPaymentTerms: number) => {
    setValue(paymentDueDaysRegister.name, newPaymentTerms);
  };

  return (
    <ThemeProvider theme={muiDefaultTheme}>
      <section className="flex h-full flex-col gap-y-4 px-2">
        <span className='text-base font-medium'>
          One off invoice
        </span>

        <div className="flex h-full flex-col overflow-y-auto rounded-lg border-[#DFEBF1] bg-white py-8">
          <div className="mx-auto flex w-full max-w-lg flex-col gap-y-8">
            <section className="flex flex-col gap-y-3">
              <label className="text-sm">Invoice details</label>

              <PlanSettingsSelect
                label='Customer'
                items={customers.map(({ id, name }) => ({ label: name, value: id }))}
                value={customerName}
                {...customerIdRegister}
                onChange={handleCustomerIdChange}
                className="w-full"
                mode={PlanSettingsSelectMode.Search}
              />

              <Controller
                name={startPeriodRegister.name}
                render={({ field }) => (
                  <DatePicker
                    value={dayjs(field.value)}
                    onChange={(newDate) => {
                      if (isNotNullOrUndefined(newDate)) {
                        const startOfNewDate = dayjs(newDate).startOf('day').utc(true).toDate();
                        setValue(startPeriodRegister.name, startOfNewDate.toISOString());
                      }
                    }}
                    sx={{
                      '& .MuiInputBase-root': {
                        paddingX: '22px',
                        borderRadius: '10px',
                        '& fieldset': {
                          borderColor: '#E8F2F8',
                        },
                        '&:hover fieldset': {
                          borderColor: '#E8F2F8',
                        },
                        '&.Mui-focused fieldset': {
                          border: '1px solid #E8F2F8',
                        },
                      },
                    }}
                    slotProps={{
                      textField: {
                        inputProps: {
                          sx: {
                            textAlign: 'end',
                            '& .MuiInputBase-root': {
                              borderColor: 'yellow !important',
                              borderRadius: '80px',
                              '& fieldset': {
                                borderColor: 'red !important',
                              },
                              '&:hover fieldset': {
                                borderColor: 'green !important',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: 'purple !important',
                              },
                            },
                          },
                        },
                        InputProps: {
                          startAdornment: (
                            <InputAdornment
                              position="start"
                            >
                              <Typography color="#243353">Date</Typography>
                            </InputAdornment>
                          ),
                        },
                      },
                    }}
                  />
                )}
              />

              <PlanSettingsSelect
                label='Due date'
                items={[
                  {
                    label: 'Immediate',
                    value: 0,
                  },
                  {
                    label: 'Net 15',
                    value: 15,
                  },
                  {
                    label: 'Net 30',
                    value: 30,
                  },
                  {
                    label: 'Net 60',
                    value: 60,
                  },
                ]}
                value={paymentTerms}
                {...paymentDueDaysRegister}
                onChange={handlePaymentDueDaysChange}
                className="w-full"
              />

              <TrialDurationAutoComplete
                control={control}
                name={trialPeriodRegister.name}
                freeTrial={false}
              />

              <Box sx={{
                border: '1px solid #E8F2F8',
                borderRadius: '10px',
                padding: '10px 20px',
              }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography>PO. Number</Typography>

                  <TextField
                    variant="standard"
                    placeholder="input text"
                    {...purchaseOrderRegister}
                    InputProps={{
                      disableUnderline: true,
                    }}
                    sx={{
                      maxWidth: '80px',
                    }}
                  />
                </Stack>
              </Box>
            </section>
          </div>
        </div>
      </section>
    </ThemeProvider>
  );
};